/**
 * Local, per-device preferences. Never on the wire — the server has no opinion
 * about how big a viewer draws their cards.
 *
 * Card size is the one required accessibility control (DESIGN §3.5): it maps to
 * `data-cards` on <html>, which is what the whole geometry scale hangs off, so
 * changing it re-flows the fan, the trick and the hand band together.
 */
import { useEffect, useState } from 'react'
import { DEFAULT_DECK, type DeckName } from '../cards/assets'

export type CardScale = 's' | 'm' | 'l'
export type MotionPref = 'system' | 'reduce'
/** Which end of the fan the strongest cards — and so the trump block — sit at.
 *  Trickster calls the same setting `HandSortStyle.highOnRight/highOnLeft`; the
 *  game module does the sorting and this only says whether to read its answer
 *  forwards or backwards, so it costs no game module a line. */
export type HandOrder = 'highRight' | 'highLeft'

export interface Settings {
  cardScale: CardScale
  deck: DeckName
  handOrder: HandOrder
  motion: MotionPref
  sounds: boolean
  /** The table answers the phone being moved: the trick drifts, shadows swing
   *  and the cards catch the lamp (table/tilt.ts). Off by default, because on
   *  iOS turning it on is a permission prompt, and that should be asked for. */
  tilt: boolean
  /** Cards can be picked up: drag one out of the hand and let go upwards to
   *  throw it, or push a card on the felt about (table/touch.ts). A tap still
   *  plays, so this only ever adds a way. */
  flick: boolean
  /** A short buzz when a card is picked up or thrown and when your turn comes
   *  round. Only where the browser can vibrate — not iOS Safari. */
  haptics: boolean
  /** The others at the table see a card you pick up or nudge move, and see
   *  that it is you holding it (table/gestures.ts). Never which card from your
   *  hand — see shared/protocol/gesture.ts. */
  shareMoves: boolean
  /** Draw the cards the others are handling (table/RemoteHands.tsx). */
  seeMoves: boolean
  /** Fingers on the empty felt glow with the lamp's light (table/FeltLight.tsx),
   *  shared and seen under the same two settings as a card's moves. */
  feltTouch: boolean
  /** Show coach hints at tables whose host allows them (Lobby). On by
   *  default, so allowing it in the lobby is enough; the Coach button over
   *  the hand hides them for this player. */
  coach: boolean
}

const KEY = 'cards.settings.v1'

const DEFAULTS: Settings = {
  cardScale: 'm',
  deck: DEFAULT_DECK,
  handOrder: 'highRight',
  motion: 'system',
  sounds: true,
  tilt: false,
  flick: true,
  haptics: true,
  shareMoves: true,
  seeMoves: true,
  feltTouch: true,
  coach: true,
}

function read(): Settings {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return { ...DEFAULTS }
    return { ...DEFAULTS, ...(JSON.parse(raw) as Partial<Settings>) }
  } catch {
    return { ...DEFAULTS }
  }
}

let current: Settings = read()
const subscribers = new Set<(s: Settings) => void>()

export function getSettings(): Settings {
  return current
}

/** Mirror onto <html> so CSS owns every consequence of a setting. */
export function applySettings(s: Settings = current): void {
  const root = document.documentElement
  root.dataset.cards = s.cardScale
  root.dataset.deck = s.deck
  if (s.motion === 'reduce') root.dataset.motion = 'reduce'
  else delete root.dataset.motion
  if (s.flick) root.dataset.flick = ''
  else delete root.dataset.flick
}

export function setSettings(patch: Partial<Settings>): void {
  current = { ...current, ...patch }
  try {
    localStorage.setItem(KEY, JSON.stringify(current))
  } catch {
    /* private mode: the session still honours the change, it just won't persist */
  }
  applySettings(current)
  for (const fn of subscribers) fn(current)
}

/** For the few things outside React that follow a setting (table/tilt.ts). */
export function subscribeSettings(fn: (s: Settings) => void): () => void {
  subscribers.add(fn)
  return () => {
    subscribers.delete(fn)
  }
}

export function useSettings(): [Settings, (patch: Partial<Settings>) => void] {
  const [s, setS] = useState(current)
  useEffect(() => {
    const fn = (next: Settings) => setS(next)
    subscribers.add(fn)
    fn(current)
    return () => {
      subscribers.delete(fn)
    }
  }, [])
  return [s, setSettings]
}

export function prefersReducedMotion(): boolean {
  if (current.motion === 'reduce') return true
  return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches
}
